// src/crm/components/SocialEditor.tsx
import type { Social } from '../../lib/types'

export type SocialDraft = Omit<Social, 'id' | 'creatorId' | 'updatedAt'> & { _key: string }

interface SocialEditorProps {
  value: SocialDraft[]
  onChange: (socials: SocialDraft[]) => void
}

const PLATFORMS = ['instagram', 'tiktok', 'youtube', 'twitter', 'x', 'linkedin', 'facebook', 'autre']

const inputStyle: React.CSSProperties = {
  background: 'var(--elevated)',
  border: '1px solid var(--line-strong)',
  borderRadius: '6px',
  padding: '0.375rem 0.5rem',
  color: 'var(--fg)',
  fontSize: '0.8125rem',
  outline: 'none',
  width: '100%',
}

let keyCounter = 0
function newKey() { return `social-${++keyCounter}` }

export function SocialEditor({ value, onChange }: SocialEditorProps) {
  function add() {
    onChange([
      ...value,
      {
        _key: newKey(),
        platform: 'instagram',
        handle: null,
        profileUrl: null,
        followers: null,
        engagementRate: null,
      },
    ])
  }

  function remove(key: string) {
    onChange(value.filter((s) => s._key !== key))
  }

  function update(key: string, patch: Partial<SocialDraft>) {
    onChange(value.map((s) => s._key === key ? { ...s, ...patch } : s))
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Réseaux sociaux
        </span>
        <button
          type="button"
          onClick={add}
          style={{
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '5px',
            padding: '0.25rem 0.625rem',
            color: 'var(--bronze)',
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Ajouter un réseau
        </button>
      </div>

      {value.length === 0 && (
        <p style={{ color: 'var(--dim)', fontSize: '0.8125rem', margin: 0 }}>
          Aucun réseau social. Cliquez sur "Ajouter un réseau".
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {value.map((s, idx) => {
          const platformId = `social-platform-${idx}`
          const handleId = `social-handle-${idx}`
          const profileUrlId = `social-profileurl-${idx}`
          const followersId = `social-followers-${idx}`
          const engagementId = `social-engagement-${idx}`
          const datalistId = `platforms-${idx}`

          return (
            <div
              key={s._key}
              style={{
                background: 'var(--elevated)',
                border: '1px solid var(--line)',
                borderRadius: '8px',
                padding: '0.75rem',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.5rem',
              }}
            >
              <datalist id={datalistId}>
                {PLATFORMS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>

              {/* Ligne 1 : Plateforme + Handle */}
              <div>
                <label
                  htmlFor={platformId}
                  style={{ display: 'block', fontSize: '0.75rem', color: 'var(--dim)', marginBottom: '0.25rem' }}
                >
                  Plateforme
                </label>
                <input
                  id={platformId}
                  type="text"
                  list={datalistId}
                  value={s.platform}
                  onChange={(e) => update(s._key, { platform: e.target.value })}
                  placeholder="instagram"
                  style={inputStyle}
                />
              </div>
              <div>
                <label
                  htmlFor={handleId}
                  style={{ display: 'block', fontSize: '0.75rem', color: 'var(--dim)', marginBottom: '0.25rem' }}
                >
                  Handle (@username)
                </label>
                <input
                  id={handleId}
                  type="text"
                  value={s.handle ?? ''}
                  onChange={(e) => update(s._key, { handle: e.target.value || null })}
                  placeholder="@username"
                  style={inputStyle}
                />
              </div>

              {/* Ligne 2 : URL profil */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label
                  htmlFor={profileUrlId}
                  style={{ display: 'block', fontSize: '0.75rem', color: 'var(--dim)', marginBottom: '0.25rem' }}
                >
                  URL profil
                </label>
                <input
                  id={profileUrlId}
                  type="url"
                  value={s.profileUrl ?? ''}
                  onChange={(e) => update(s._key, { profileUrl: e.target.value || null })}
                  placeholder="https://..."
                  style={inputStyle}
                />
              </div>

              {/* Ligne 3 : Abonnés + Taux d'engagement */}
              <div>
                <label
                  htmlFor={followersId}
                  style={{ display: 'block', fontSize: '0.75rem', color: 'var(--dim)', marginBottom: '0.25rem' }}
                >
                  Abonnés
                </label>
                <input
                  id={followersId}
                  type="number"
                  min={0}
                  value={s.followers ?? ''}
                  onChange={(e) => update(s._key, { followers: e.target.value ? Number(e.target.value) : null })}
                  placeholder="0"
                  style={inputStyle}
                />
              </div>
              <div>
                <label
                  htmlFor={engagementId}
                  style={{ display: 'block', fontSize: '0.75rem', color: 'var(--dim)', marginBottom: '0.25rem' }}
                >
                  Engagement (%)
                </label>
                <input
                  id={engagementId}
                  type="number"
                  min={0}
                  step={0.01}
                  value={s.engagementRate ?? ''}
                  onChange={(e) => update(s._key, { engagementRate: e.target.value ? Number(e.target.value) : null })}
                  placeholder="3.5"
                  style={inputStyle}
                />
              </div>

              {/* Supprimer */}
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => remove(s._key)}
                  style={{
                    background: 'rgba(217,83,30,0.1)',
                    border: '1px solid rgba(217,83,30,0.2)',
                    borderRadius: '5px',
                    padding: '0.25rem 0.625rem',
                    color: 'var(--arancio)',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                  }}
                >
                  Supprimer
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
